import { Eyebrow, TitleBar } from './atoms'
import { useLang, t } from '../lib/lang'
import { resolveImageUrl, externalUrl } from '../lib/api'
import type { Singolo } from '../lib/types'

function toSpotifyEmbed(url: string | null): string | null {
  if (!url) return null
  try {
    const u = new URL(url)
    if (u.hostname !== 'open.spotify.com') return null
    const parts = u.pathname.split('/').filter(Boolean)
    const kinds = ['track', 'album', 'playlist', 'artist', 'episode', 'show']
    const idx = parts.findIndex((p) => kinds.includes(p))
    if (idx < 0) return null
    const kind = parts[idx]
    const id = parts[idx + 1]
    if (!id) return null
    return `https://open.spotify.com/embed/${kind}/${id}?utm_source=generator`
  } catch {
    return null
  }
}

function fallbackListen(s: Singolo): string | null {
  return externalUrl(s.spotify_url ?? s.youtube_url ?? s.apple_url ?? null)
}

function Row({ item, last }: { item: Singolo; last: boolean }) {
  const { lang } = useLang()
  const spotifyEmbed = toSpotifyEmbed(item.spotify_url)
  const listenUrl = fallbackListen(item)
  const releaseYear = item.release_date ? item.release_date.slice(0, 4) : null

  const cover = (
    <div className="ax-singolo-cover" style={{
      width: 160, aspectRatio: '1', flexShrink: 0,
      overflow: 'hidden', background: 'var(--ink-2)',
      boxShadow: 'var(--shadow-cover)',
    }}>
      {item.cover_image_key && (
        <img src={resolveImageUrl(item.cover_image_key)} alt={item.title}
             style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
      )}
    </div>
  )

  return (
    <li className="ax-singolo-row" style={{
      display: 'flex', gap: 32, alignItems: 'center',
      padding: '36px 0',
      borderBottom: last ? 'none' : '1px solid var(--line-inverse)',
    }}>
      {listenUrl && !spotifyEmbed ? (
        <a href={listenUrl} target="_blank" rel="noopener noreferrer" style={{ display: 'block', textDecoration: 'none' }}>
          {cover}
        </a>
      ) : cover}

      <div className="ax-singolo-body" style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontFamily: 'var(--font-body)', fontSize: 10,
          letterSpacing: '0.2em', textTransform: 'uppercase',
          color: 'var(--fg-soft)', marginBottom: 6,
        }}>{releaseYear ?? ''}{releaseYear ? ' · ' : ''}Single</div>
        <div style={{
          fontFamily: 'var(--font-display)', fontWeight: 500,
          textTransform: 'uppercase', letterSpacing: '0.04em',
          fontSize: 24, lineHeight: 1.08, color: 'var(--fg-inverse)',
        }}>{item.title}</div>
        {item.original_artist && (
          <div style={{
            fontFamily: 'var(--font-body)', fontSize: 14,
            color: 'var(--fg-inverse-muted)', marginTop: 8,
          }}>{item.original_artist} · {t(lang, 'single_suffix')}</div>
        )}

        {spotifyEmbed && (
          <div style={{ marginTop: 18 }}>
            <iframe
              src={spotifyEmbed}
              title={`Spotify — ${item.title}`}
              width="100%" height="80"
              frameBorder={0}
              loading="lazy"
              allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
              style={{ borderRadius: 4, display: 'block', colorScheme: 'normal' }}
            />
          </div>
        )}
      </div>
    </li>
  )
}

export function Singoli({ items }: { items: Singolo[] }) {
  const { lang } = useLang()
  if (!items.length) return null
  return (
    <section id="singoli" className="ax-pad-section" style={{
      background: 'var(--ink)', color: 'var(--fg-inverse)', padding: '120px 48px',
    }}>
      <div style={{ maxWidth: 1080, margin: '0 auto' }}>
        <div className="ax-header-row" style={{
          display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between',
          marginBottom: 40, gap: 40, flexWrap: 'wrap',
        }}>
          <div>
            <Eyebrow light>{t(lang, 'section_singoli_eyebrow')}</Eyebrow>
            <TitleBar size={56} light>
              {t(lang, 'section_singoli_title_a')}<br />{t(lang, 'section_singoli_title_b')}
            </TitleBar>
          </div>
          <p style={{
            fontFamily: 'var(--font-body)', fontSize: 15, lineHeight: 1.6,
            color: 'var(--fg-inverse-muted)', maxWidth: 380, margin: 0,
          }}>{t(lang, 'section_singoli_intro')}</p>
        </div>

        <ul style={{
          listStyle: 'none', padding: 0, margin: 0,
          borderTop: '1px solid var(--line-inverse)',
        }}>
          {items.map((s, i) => (
            <Row key={s.id} item={s} last={i === items.length - 1} />
          ))}
        </ul>
      </div>
    </section>
  )
}
